import React, { useMemo, useState } from 'react';

import { Modal } from '@/components/Modal';
import {
  useWizard,
  useWizardFooterConfig,
  WizardModalFooter,
  WizardProgressBar,
  WizardStepIndicator,
} from '@/components/wizards';
import {
  DEFAULT_FLAGS,
  DEFAULT_FORM_DATA,
} from '@/sections/customerManagement/constants';

import {
  AddCustomerModalProps,
  WizardFormData,
} from './AddCustomerModal.types';
import { useCustomerSubmit } from './hooks';
import {
  AttackSurfaceStep,
  BasicInformationStep,
  ConfigurationStep,
  CustomerUsersStep,
  PraetorianUsersStep,
  ScanFrequencyStep,
  SubscriptionPricingStep,
} from './steps';
import { customerWizardSteps } from './wizardConfig';

/**
 * AddCustomerModal - A wizard-based modal for creating new customers
 * Migrated to use the generalized wizard system from @/components/wizards
 */
export const AddCustomerModal: React.FC<AddCustomerModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultCustomerType,
}) => {
  // Local state for enabledFlags (not part of core wizard)
  const [enabledFlags, setEnabledFlags] = useState<string[]>(DEFAULT_FLAGS);

  // Compute initial data, pre-setting customer_type when defaultCustomerType is provided
  const initialData = useMemo(() => {
    if (defaultCustomerType) {
      return { ...DEFAULT_FORM_DATA, customer_type: defaultCustomerType };
    }
    return DEFAULT_FORM_DATA;
  }, [defaultCustomerType]);

  // Use the generalized wizard hook
  const wizard = useWizard<WizardFormData>({
    steps: customerWizardSteps,
    initialData,
  });

  // Customer submission logic (kept separate from wizard)
  const { handleSubmit, isPending } = useCustomerSubmit({
    formData: wizard.formData,
    enabledFlags,
    onSuccess,
    onClose: () => {
      wizard.reset();
      setEnabledFlags(DEFAULT_FLAGS);
      onClose();
    },
  });

  const handleClose = () => {
    wizard.reset();
    setEnabledFlags(DEFAULT_FLAGS);
    onClose();
  };

  // Get footer configuration using the generalized hook
  const footerConfig = useWizardFooterConfig({
    canGoNext: wizard.navigation.canGoNext,
    canGoBack: wizard.navigation.canGoBack,
    isPending,
    isLastStep: wizard.navigation.isLastStep,
    onNext: wizard.navigation.handleNext,
    onBack: wizard.navigation.handleBack,
    onCancel: handleClose,
    onSubmit: handleSubmit,
    labels: { submit: 'Create', submitting: 'Creating...' },
  });

  // Render the current step component based on step ID
  const renderCurrentStep = () => {
    const commonProps = {
      formData: wizard.formData,
      setFormData: wizard.setFormData,
      isSubmitting: isPending,
    };

    switch (wizard.currentStepId) {
      case 'basic-info':
        return (
          <BasicInformationStep
            {...commonProps}
            defaultCustomerType={defaultCustomerType}
          />
        );
      case 'attack-surface':
        return <AttackSurfaceStep {...commonProps} />;
      case 'configuration':
        return (
          <ConfigurationStep
            {...commonProps}
            enabledFlags={enabledFlags}
            setEnabledFlags={setEnabledFlags}
          />
        );
      case 'scan-frequency':
        return <ScanFrequencyStep {...commonProps} />;
      case 'subscription-pricing':
        return <SubscriptionPricingStep {...commonProps} />;
      case 'praetorian-users':
        return <PraetorianUsersStep {...commonProps} />;
      case 'customer-users':
        return <CustomerUsersStep {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={
        <div className="flex w-full items-baseline justify-between">
          <span>
            {defaultCustomerType
              ? 'Add Bug Bounty Program'
              : 'Add New Customer'}
          </span>
          <WizardStepIndicator
            currentStep={wizard.progress.currentDisplayStep}
            totalSteps={wizard.progress.totalDisplaySteps}
            stepTitle={wizard.progress.currentStepTitle}
          />
        </div>
      }
      closeOnOutsideClick={false}
      size="3xl"
      className="w-full max-w-[900px]"
      hideFooter={true}
    >
      <WizardProgressBar
        currentStep={wizard.progress.currentDisplayStep}
        totalSteps={wizard.progress.totalDisplaySteps}
        stepTitle={wizard.progress.currentStepTitle}
      />
      {renderCurrentStep()}

      {/* Wizard-specific footer with proper button layout */}
      <WizardModalFooter config={footerConfig} />
    </Modal>
  );
};

export default AddCustomerModal;
